import { useState, useEffect, useRef } from 'react';
import { useGlobal } from '../contexts/GlobalContext';
import Button from './Button';


function CourseCard({ image, title, date, description, calendlyEmbed, callToActionButtonText }) {
  const { t } = useGlobal();
  const iUrl = import.meta.env.VITE_DIRECTUS_PUBLIC_URL + "/assets/"
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
  const [isCalendlyLoaded, setIsCalendlyLoaded] = useState(false);
  const embedRef = useRef(null);

  const toggleCalendly = () => {
    setIsCalendlyOpen(!isCalendlyOpen);
    if (!isCalendlyLoaded) {
      setIsCalendlyLoaded(true);
    }
  };

  useEffect(() => {
    if (isCalendlyOpen && embedRef.current) {
      embedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

      // Initialize Calendly widget after HTML is injected
      if (window.Calendly) {
        // Find the calendly div that was just rendered
        const calendlyDiv = embedRef.current.querySelector('.calendly-inline-widget');
        if (calendlyDiv && calendlyDiv.dataset.url) {
          // Initialize the widget manually
          window.Calendly.initInlineWidget({
            url: calendlyDiv.dataset.url,
            parentElement: calendlyDiv
          });
        }
      }
    }
  }, [isCalendlyOpen, isCalendlyLoaded]);

  return (
    <div className="course-card shadow-lg overflow-hidden w-full lg:w-[70%] mx-auto">
      <div className="course-card__container flex flex-col lg:grid lg:grid-cols-5">
        {/* Image Section - Full width on mobile, 20% on desktop */}
        <div className="course-card__image-wrapper lg:col-span-1 h-[300px] lg:h-auto">
          <img
            src={`${iUrl}${image}?format=webp`}
            alt={title}
            className="course-card__image w-full h-full object-cover"
          />
        </div>

        {/* Content Section - Full width on mobile, 80% on desktop */}
        <div className="course-card__content lg:col-span-4 p-8 flex flex-col justify-center">
          <h3 className="font-light course-card__title font-family-header text-3xl text-dark-900 mb-2">
            {title}
          </h3>
          <p className="course-card__date font-body text-sm text-dark-500 mb-3">
            {date}
          </p>
          <div className="course-card__divider w-full h-px bg-gray-300 mb-3"></div>
          <p className="course-card__description font-body text-dark-700 leading-relaxed">
            {description}
          </p>
          <div className="flex justify-center lg:justify-end mt-4">
            {calendlyEmbed ? (
              <button
                onClick={toggleCalendly}
                className="course-card__toggle-btn w-fit border border-solid text-white font-display font-extralight transition-colors cursor-pointer flex items-center text-sm px-6 py-2 bg-secondary-500 border-secondary-500 hover:text-secondary-500 hover:bg-white"
                data-umami-event="Course Calendly click"
                data-umami-event-name={ title }
              >
                { callToActionButtonText }
              </button>
            ) : (
              <Button className="calendly-fallback-button" size="small" target="_blank" rel="noopener noreferrer">
                { callToActionButtonText }
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Calendly Embed Section */}
      {calendlyEmbed && (
        <div
          ref={embedRef}
          className={`course-card__calendly-wrapper overflow-hidden transition-all duration-500 ease-in-out ${
            isCalendlyOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="course-card__calendly-container p-8 pt-0">
            {isCalendlyLoaded && (
              <div
                className="course-card__calendly-embed"
                dangerouslySetInnerHTML={{ __html: calendlyEmbed }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CourseCard
